import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PasswordsService } from '../core/passwords.service';
import { AuthService } from '../core/auth.service';
import { LanguageService } from '../core/language.service';

interface ForceChangeState {
  userId: string;
  username: string;
  currentPassword: string;
}

@Component({
  standalone: true,
  selector: 'app-force-change-password',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './force-change-password.component.html',
  styleUrls: ['./login.component.scss'],
})
export class ForceChangePasswordComponent implements OnInit {
  private router = inject(Router);
  private api = inject(PasswordsService);
  private auth = inject(AuthService);
  private ms = inject(MessageService);
  private translate = inject(TranslateService);
  private fb = inject(FormBuilder);
  lang = inject(LanguageService);

  state: ForceChangeState | null = null;
  form!: FormGroup;
  hideNew = true;
  hideConfirm = true;
  loading = false;
  submitted = false;
  error = '';

  ngOnInit() {
    const nav = this.router.getCurrentNavigation()?.extras?.state as ForceChangeState | undefined;
    const histState = (history.state ?? {}) as Partial<ForceChangeState>;
    const merged: Partial<ForceChangeState> = nav ?? histState;

    if (!merged?.userId || !merged?.username || !merged?.currentPassword) {
      this.router.navigateByUrl('/login');
      return;
    }

    this.state = {
      userId: merged.userId,
      username: merged.username,
      currentPassword: merged.currentPassword,
    };

    this.form = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
    });
  }

  get f() { return this.form.controls; }

  get passwordsMismatch(): boolean {
    const a = this.form?.get('newPassword')?.value;
    const b = this.form?.get('confirmPassword')?.value;
    return !!a && !!b && a !== b;
  }

  backToLogin() {
    this.router.navigateByUrl('/login');
  }

  onSubmit() {
    this.submitted = true;
    this.error = '';

    if (!this.state) return;
    if (this.form.invalid || this.passwordsMismatch) return;

    const newPassword = this.f['newPassword'].value as string;
    if (newPassword === this.state.currentPassword) {
      this.error = 'كلمة المرور الجديدة يجب أن تختلف عن الحالية.';
      return;
    }

    this.loading = true;
    this.api.change(this.state.userId, this.state.currentPassword, newPassword).subscribe({
      next: async () => {
        try {
          await this.auth.loginWithPassword(this.state!.username, newPassword, 'AngularSPA', false);
          this.ms.add({
            severity: 'success',
            summary: this.translate.instant('COMMON.SUCCESS'),
            detail: this.translate.instant('CHANGE_PASSWORD.SUCCESS'),
          });
          this.router.navigateByUrl('/');
        } catch {
          this.loading = false;
          this.router.navigate(['/login'], { queryParams: { changed: '1' } });
        }
      },
      error: (e) => {
        this.loading = false;
        const errs = e?.error?.errors;
        if (errs) {
          const messages: string[] = [];
          for (const v of Object.values(errs) as unknown[]) {
            if (Array.isArray(v)) messages.push(...(v as string[]));
            else if (typeof v === 'string') messages.push(v);
          }
          this.error = messages.join(' • ');
        } else {
          this.error = e?.error?.message || this.translate.instant('CHANGE_PASSWORD.ERROR');
        }
      },
    });
  }
}
